import { useState, useEffect } from 'react';
import {
    Megaphone,
    Send,
    Clock,
    CheckCircle2,
    FileText,
    AlertCircle,
    ExternalLink
} from "lucide-react";
import {
    collection,
    addDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export function FinalFeedbackTab({ user }) {
    // Deadline: December 12, 2025 at 12:00 PM (Noon)
    const DEADLINE = new Date('2025-12-12T12:00:00');

    const [isExpired, setIsExpired] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [answers, setAnswers] = useState({
        q1: '', // Does the proposed vision reflect what you value?
        q2: '', // Changes or improvements?
        q3: '', // Ideas or opportunities missing?
        q4: ''  // What feels most important?
    });

    useEffect(() => {
        // Check if already submitted
        const hasSubmitted = localStorage.getItem('minersParkFeedbackSubmitted');
        if (hasSubmitted) {
            setSubmitted(true);
        }

        const checkTime = () => {
            const now = new Date();
            if (now > DEADLINE) {
                setIsExpired(true);
                setTimeLeft('Feedback Closed');
            } else {
                const diff = DEADLINE - now;
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                setTimeLeft(`${days}d ${hours}h ${minutes}m remaining`);
            }
        };

        checkTime();
        const timer = setInterval(checkTime, 60000);
        return () => clearInterval(timer);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isExpired) return;

        setSubmitting(true);
        try {
            await addDoc(collection(db, 'final_feedback'), {
                ...answers
                // Removed time, userId, and userEmail as requested
            });

            // Mark as submitted in local storage
            localStorage.setItem('minersParkFeedbackSubmitted', 'true');

            setSubmitted(true);
            setAnswers({ q1: '', q2: '', q3: '', q4: '' });
            // Scroll to top of form to show success message
            document.getElementById('feedback-form-section')?.scrollIntoView({ behavior: 'smooth' });
        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert('Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setAnswers(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const scrollToFeedback = () => {
        document.getElementById('feedback-form-section')?.scrollIntoView({ behavior: 'smooth' });
    };

    const getStatusBadgeColor = (expired) => {
        return expired
            ? "bg-red-500/10 text-red-400 border-red-500/20"
            : "bg-secondary/20 text-secondary border-secondary/30";
    };

    return (
        <div className="space-y-8 pb-24">
            {/* Header Section */}
            <div className="section-card">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="section-title flex items-center gap-3">
                            <Megaphone className="h-6 w-6 text-secondary" />
                            Final Feedback: Miners Park Plan
                        </h2>
                        <p className="section-text mt-2">
                            We are seeking your final thoughts on the proposed Master Plan.
                            Please review the plan below and answer the questions to help shape the future of Miners Park.
                        </p>
                    </div>

                    <div className="flex flex-col items-end gap-3 min-w-fit">
                        <Badge variant="outline" className={`flex items-center gap-2 px-4 py-2 ${getStatusBadgeColor(isExpired)}`}>
                            <Clock className="h-4 w-4" />
                            <div className="flex flex-col items-end">
                                <span className="font-bold uppercase text-xs tracking-wider">
                                    {isExpired ? 'Feedback Closed' : 'Deadline: Dec 12, 12:00 PM'}
                                </span>
                                {!isExpired && <span className="text-[10px] opacity-80 font-mono">{timeLeft}</span>}
                            </div>
                        </Badge>
                    </div>
                </div>
            </div>

            {/* PDF Viewer Section */}
            <Card className="bg-black/40 border-white/10 overflow-hidden">
                <CardHeader className="bg-white/5 border-b border-white/10 pb-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2 text-white">
                                <FileText className="h-5 w-5 text-secondary" />
                                Master Plan Document
                            </CardTitle>
                            <CardDescription className="text-gray-400">
                                Scroll to view • Use browser controls to zoom
                            </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2" onClick={() => window.open('/pdfs/Miners_Park_Plan.pdf', '_blank')}>
                            <ExternalLink className="h-4 w-4" />
                            Open in New Tab
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0 bg-white/5 min-h-[800px] relative flex items-center justify-center">
                    <object
                        data="/pdfs/Miners_Park_Plan.pdf"
                        type="application/pdf"
                        className="w-full h-[800px]"
                    >
                        <div className="text-center p-12">
                            <p className="text-xl text-gray-400 mb-4">PDF Viewer not supported or file missing.</p>
                            <Button
                                variant="outline"
                                className="gap-2 text-secondary hover:text-white"
                                onClick={() => window.open('/pdfs/Miners_Park_Plan.pdf', '_blank')}
                            >
                                Download the Plan (PDF)
                            </Button>
                        </div>
                    </object>
                </CardContent>
            </Card>

            <div className="flex justify-center py-12">
                <div className="bg-black/40 border border-white/10 rounded-lg p-8 text-center max-w-2xl">
                    <CheckCircle2 className="h-12 w-12 text-secondary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Feedback Now Closed</h3>
                    <p className="text-gray-400">
                        Thank you to everyone who submitted their feedback on the Miners Park Master Plan.
                    </p>
                </div>
            </div>
        </div>
    );
}
